import React from 'react';
import { Sparkle, TrendUp, Buildings, Briefcase, Vault } from '@phosphor-icons/react';

const RevenueStreams = () => {
  const streams = [
    {
      icon: TrendUp,
      title: 'Dividend Investing',
      description: 'Generate passive income through dividend-paying stocks and funds',
      considerations: ['Tax implications of dividend income', 'Diversification across sectors', 'Dividend growth history', 'Reinvestment strategies']
    },
    {
      icon: Buildings,
      title: 'Real Estate Investing',
      description: 'Rental income and property appreciation opportunities',
      considerations: ['Property management responsibilities', 'Market location analysis', 'Financing options and leverage', 'Tax benefits and deductions']
    },
    {
      icon: Briefcase,
      title: 'Business Ownership',
      description: 'Active or passive business income streams',
      considerations: ['Time commitment required', 'Market demand and competition', 'Scalability potential', 'Exit strategy planning']
    },
    {
      icon: Vault,
      title: 'Cash Value Strategies',
      description: 'Life insurance and annuity products with cash value',
      considerations: ['Policy fees and costs', 'Growth potential vs guarantees', 'Liquidity and access', 'Estate planning benefits']
    }
  ];

  return (
    <div className="space-y-8 fade-in">
      <div className="bg-[#C5A059]/10 border border-[#C5A059]/30 rounded-md p-4">
        <div className="flex items-start gap-3">
          <Sparkle size={20} weight="duotone" className="text-[#C5A059] flex-shrink-0 mt-0.5" />
          <p className="text-sm text-[#4A5568]">
            <strong className="text-[#1A202C]">Educational Information Only:</strong> This page provides general education about revenue streams. Not investment advice. Consult qualified financial, tax, and legal professionals before making investment decisions.
          </p>
        </div>
      </div>

      <div>
        <h1 className="cabinet-grotesk text-3xl sm:text-4xl tracking-tight font-medium text-[#0A1128] mb-2">
          Revenue Stream Planning
        </h1>
        <p className="text-base text-[#4A5568]">
          Explore alternative income strategies for retirement
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {streams.map((stream, idx) => {
          const Icon = stream.icon;
          return (
            <div key={idx} className="bg-white border border-slate-200 rounded-md shadow-sm p-6 dashboard-card" data-testid={`revenue-stream-${idx}`}>
              <div className="flex items-start gap-4 mb-4">
                <div className="p-3 bg-[#C5A059]/10 rounded-md">
                  <Icon size={28} weight="duotone" className="text-[#C5A059]" />
                </div>
                <div className="flex-1">
                  <h2 className="cabinet-grotesk text-xl font-medium text-[#0A1128] mb-2">{stream.title}</h2>
                  <p className="text-sm text-[#4A5568]">{stream.description}</p>
                </div>
              </div>
              
              <div className="pt-4 border-t border-slate-200">
                <h3 className="text-xs font-semibold uppercase tracking-[0.1em] text-[#4A5568] mb-3">Key Considerations</h3>
                <ul className="space-y-2">
                  {stream.considerations.map((consideration, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-[#1A202C]">
                      <span className="text-[#C5A059] mt-1">•</span>
                      <span>{consideration}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          );
        })}
      </div>

      <div className="bg-white border border-slate-200 rounded-md shadow-sm p-6 dashboard-card">
        <h2 className="cabinet-grotesk text-xl font-medium text-[#0A1128] mb-4">Important Reminders</h2>
        <div className="space-y-3 text-sm text-[#4A5568]">
          <p><strong className="text-[#1A202C]">Diversification:</strong> Consider spreading investments across multiple income streams to reduce risk.</p>
          <p><strong className="text-[#1A202C]">Tax Planning:</strong> Different revenue streams have different tax implications. Work with a tax professional.</p>
          <p><strong className="text-[#1A202C]">Risk Assessment:</strong> Understand your risk tolerance and time horizon before committing capital.</p>
          <p><strong className="text-[#1A202C]">Professional Guidance:</strong> These strategies often benefit from professional financial advice tailored to your situation.</p>
        </div>
      </div>
    </div>
  );
};

export default RevenueStreams;
